<template>
  <div class="data-table-wrapper">
    <div class="section-header">
      <h3>{{ title }}</h3>
      <div v-if="stats && stats.length" class="section-stats">
        <span v-for="(item, index) in stats" :key="index" class="stat-item">
          <span class="stat-label">{{ item.label }}</span>
          <span class="stat-value">{{ item.value }}</span>
        </span>
      </div>
    </div>
    <div ref="scrollContainer" class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index">{{ header.name }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(record, index) in [...records, ...records]"
            :key="index"
            :class="{ 'even': index % 2 === 0, 'odd': index % 2 !== 0 }"
            class="table-row"
          >
            <td v-for="(header, hIndex) in headers" :key="hIndex">
              <slot :name="'cell_' + header.field_name" :record="record" :header="header" :index="index">
                {{ record[header.field_name] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    title: {
      type: String,
      default: ''
    },
    headers: {
      type: Array,
      default: () => []
    },
    records: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    startAutoScroll() {
      const container = this.$refs.scrollContainer
      if (!container) return

      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      if (scrollHeight <= clientHeight) return

      const halfHeight = scrollHeight / 2
      let scrollTop = 0

      const scroll = () => {
        scrollTop += 1
        if (scrollTop >= halfHeight) {
          container.scrollTop = 0
          scrollTop = 0
        }
        container.scrollTop = scrollTop
      }

      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
      }
      this.scrollTimer = setInterval(scroll, 30)
    }
  },
  beforeDestroy() {
    if (this.scrollTimer) {
      clearInterval(this.scrollTimer)
    }
  }
}
</script>

<style scoped>
.data-table-wrapper {
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
}

.section-stats {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-left: auto;
}

.stat-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #a0aec0;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #63b3ed;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.table-container {
  flex: 1;
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
  color: #a0aec0;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  position: sticky;
  top: 0;
  background: rgba(26, 54, 93, 0.9);
}

.data-table td {
  padding: 8px 10px;
  font-size: 12px;
  color: #e2e8f0;
  transition: all 0.3s ease;
}

.table-row {
  animation: slideIn 0.3s ease-out;
}

.table-row.even {
  background: rgba(49, 130, 206, 0.1);
}

.table-row.odd {
  background: rgba(49, 130, 206, 0.05);
}

.table-row:hover {
  background: rgba(49, 130, 206, 0.2);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>